<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";
import { UiButton, UiIcon, UiText, UiAside, UiDialog, UiSelect } from "@shopexperts/shared";
import type { PartnerBadge } from "@shopexperts/shared";
import BadgeCard from "@/components/admin/cards/BadgeCard.vue";
import BadgeForm, { type BadgeFormModel, type BadgeFormPayload } from "@/components/admin/BadgeForm.vue";
import BadgeListingStatus from "@/components/admin/BadgeListingStatus.vue";
import AssignExpertForm from "@/components/admin/AssignExpertForm.vue";
import { useAdminBadges } from "@/app/composables/badges/useAdminBadges";
import { useCreateBadge } from "@/app/composables/badges/useCreateBadge";
import { useUpdateBadge } from "@/app/composables/badges/useUpdateBadge";
import { useDeleteBadge } from "@/app/composables/badges/useDeleteBadge";
import { useSyncBadgeListing } from "@/app/composables/badges/useSyncBadgeListing";

type BadgeWithIcon = PartnerBadge;

const search = ref('');
const selectedTag = ref('');
const asideOpen = ref(false);
const formEl = useTemplateRef('formEl');
const editingBadge = ref<BadgeWithIcon | null>(null);
const deleteConfirmOpen = ref(false);
const badgeToDelete = ref<number | null>(null);
const emptyFormModel = (): BadgeFormModel => ({ name: '', tag: null, icon: [], listing_url: '', listing_auto_update: true });
const formModel = ref<BadgeFormModel>(emptyFormModel());

const assignBadgeOpen = ref(false);
const assigningBadge = ref<BadgeWithIcon | null>(null);
const assignFormEl = useTemplateRef('assignFormEl');

const { badges, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useAdminBadges({
  search: search,
  tag: selectedTag,
  limit: 20,
});

const { mutateAsync: createBadge } = useCreateBadge();
const { mutateAsync: updateBadge } = useUpdateBadge();
const { mutateAsync: deleteBadge, isPending: isDeleting } = useDeleteBadge();
const { mutate: syncBadgeListing, isPending: isSyncingListing, syncingBadgeId } = useSyncBadgeListing();

const editingBadgeLive = computed(() => (editingBadge.value ? badges.value.find((badge) => badge.id === editingBadge.value?.id) ?? editingBadge.value : null));

function onAddClick() {
  if (editingBadge.value != null) {
    formEl.value?.resetForm();
  }
  editingBadge.value = null;
  formModel.value = emptyFormModel();
  asideOpen.value = true;
}

function onEditClick(badge: BadgeWithIcon) {
  editingBadge.value = badge;
  
  const iconItem = badge.icon 
    ? [{ 
        url: badge.icon.cdn_url || badge.icon.url || '', 
        media_id: badge.icon.id,
        isTemporary: false 
      }]
    : [];
  
  formModel.value = {
    name: badge.name,
    tag: badge.tag || null,
    icon: iconItem,
    listing_url: badge.listing_url ?? '',
    listing_auto_update: badge.listing_auto_update ?? false,
  };
  
  asideOpen.value = true;
}

function onDeleteClick(badge: BadgeWithIcon) {
  badgeToDelete.value = badge.id;
  deleteConfirmOpen.value = true;
}

function onAssignClick(badge: BadgeWithIcon) {
  assigningBadge.value = badge;
  assignBadgeOpen.value = true;
}

async function onFormSubmit(data: BadgeFormPayload) {
  try {
    const saved = editingBadge.value
      ? await updateBadge({ badgeId: editingBadge.value.id, data })
      : await createBadge(data);

    if (saved?.listing_url && saved.listing_sync?.status !== 'synced') {
      syncBadgeListing(saved.id);
    }
    formEl.value?.resetForm();
    asideOpen.value = false;
    editingBadge.value = null;
  } catch (error) {
    console.error('Error submitting badge form:', error);
  }
}

function onFormCancel() {
  formEl.value?.resetForm();
  asideOpen.value = false;
  editingBadge.value = null;
}

function onAssignSuccess() {
  // Keep form open to allow assigning to multiple experts
  // Just clear the form for next selection
  assignFormEl.value?.resetForm();
}

function onAssignCancel() {
  assignFormEl.value?.resetForm();
  assignBadgeOpen.value = false;
  assigningBadge.value = null;
}

async function confirmDelete() {
  if (!badgeToDelete.value) return;
  
  try {
    await deleteBadge(badgeToDelete.value);
    deleteConfirmOpen.value = false;
    badgeToDelete.value = null;
  } catch (error) {
    console.error('Error deleting badge:', error);
  }
}

function cancelDelete() {
  deleteConfirmOpen.value = false;
  badgeToDelete.value = null;
}

function loadMore() {
  if (hasNextPage.value && !isFetchingNextPage.value) {
    fetchNextPage();
  }
}

</script>

<template>
  <main class="flex-1 p-8 overflow-y-auto bg-secondary font-light">
      <!-- Header -->
      <div class="flex flex-col gap-4 mb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-normal">App and Theme Skills</h1>
          </div>

          <div class="flex items-center gap-3">
            <UiSelect
              v-model="selectedTag"
              :options="[
                { value: '', label: 'All tags' },
                { value: 'apps', label: 'Apps' },
                { value: 'themes', label: 'Themes' },
              ]"
              placeholder="All tags"
              size="md"
              class="w-36"
            />

            <!-- Search Input -->
            <div class="relative w-[300px]">
              <div class="absolute left-3 top-1/2 -translate-y-1/2">
                <UiIcon name="search" class="w-5 h-5 text-gray-500" />
              </div>
              <input
                v-model="search"
                type="text"
                placeholder="Search Apps/Themes ..."
                class="w-full h-8 pl-10 pr-3 py-1 border border-gray-300 rounded bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <!-- Add Button -->
            <UiButton variant="primary" label="Add App/Theme" @click="onAddClick">
              <template #before_label>
                <UiIcon name="plus-square" class="w-5 h-5 text-se-primary" />
              </template>
            </UiButton>
          </div>
        </div>
      </div>

      <!-- Badges List -->
      <div class="space-y-4">
        <div v-if="isLoading" class="text-center py-8">
          <UiText variant="p" as="p" text="Loading badges..." />
        </div>

        <div v-else-if="badges.length === 0" class="text-center py-12">
          <div class="flex flex-col items-center gap-4">
            <div>
              <UiText variant="h6" as="h6" text="No badges found" class="font-semibold" />
              <UiText variant="p-small" as="p" text="Create your first app or theme badge to get started" class="text-gray-600 mt-1" />
            </div>
            <UiButton variant="primary" label="Add App/Theme" @click="onAddClick">
                <template #icon-before>
                    <UiIcon name="plus-square" class="w-5 h-5" />
                </template>
            </UiButton>
          </div>
        </div>

        <template v-else>
          <BadgeCard
            v-for="badge in badges"
            :key="badge.id"
            :badge="badge"
            @edit="onEditClick(badge)"
            @assign="onAssignClick(badge)"
            @delete="onDeleteClick(badge)"
          />

          <!-- Load More Button -->
          <div v-if="hasNextPage" class="flex justify-center pt-4">
            <UiButton
              variant="outline"
              :label="isFetchingNextPage ? 'Loading...' : 'Load More'"
              :disabled="isFetchingNextPage"
              @click="loadMore"
            />
          </div>
        </template>
      </div>

      <!-- Form Aside -->
      <UiAside v-model="asideOpen" position="right" :close-on-overlay-click="false">
        <div class="px-4 py-6 md:p-8 flex flex-col gap-4 max-h-full overflow-y-auto">
          <div class="flex items-center justify-between">
            <UiText as="h3" :text="editingBadge ? 'Edit App/Theme' : 'Add Shopify App/Theme'" />
            <UiButton variant="ghost" shape="square" @click="onFormCancel">
              <template #before_label>
                <UiIcon name="close" class="w-6 h-6" />
              </template>
            </UiButton>
          </div>
          <UiText as="p" class="text-gray-600 mb-4" :text="editingBadge ? 'Update the app or theme details below.' : 'Submit your request, and we\'ll add the app or theme to the platform.'" />
          
          <BadgeListingStatus
            v-if="editingBadgeLive?.listing_url"
            :badge="editingBadgeLive"
            :syncing="isSyncingListing && syncingBadgeId === editingBadgeLive.id"
            @sync="syncBadgeListing(editingBadgeLive.id)"
          />

          <BadgeForm 
            ref="formEl"
            v-model="formModel"
            :submit-label="editingBadge ? 'Update App/Theme' : 'Add App/Theme'"
            @submit="onFormSubmit"
            @cancel="onFormCancel"
          />
        </div>
      </UiAside>

      <!-- Delete Confirmation Dialog -->
      <UiDialog
        v-model="deleteConfirmOpen"
        title="Delete Badge"
        text="Are you sure you want to delete this badge? This action cannot be undone."
        primary-action-label="Delete"
        secondary-action-label="Cancel"
        :primary-action-loading="isDeleting"
        @primary-action="confirmDelete"
        @secondary-action="cancelDelete"
      />

      <!-- Assign Expert Aside -->
      <UiAside v-model="assignBadgeOpen" position="right" :close-on-overlay-click="false">
        <div class="px-4 py-6 md:p-8 flex flex-col gap-4 max-h-full overflow-y-auto">
          <div class="flex items-center justify-between">
            <UiText as="h3" :text="`Assign Expert to ${assigningBadge?.name || 'Badge'}`" />
            <UiButton variant="ghost" shape="square" @click="onAssignCancel">
              <template #before_label>
                <UiIcon name="close" class="w-6 h-6" />
              </template>
            </UiButton>
          </div>
          <UiText as="p" class="text-gray-600 mb-4" text="Search and select an expert to assign this badge to their profile." />
          
          <AssignExpertForm 
            v-if="assigningBadge"
            ref="assignFormEl"
            :badge-id="assigningBadge.id"
            :badge-name="assigningBadge.name"
            @success="onAssignSuccess"
          />
        </div>
      </UiAside>
    </main>
</template>

